import { useCallback } from 'react';

/**
 * Hook for calculating shift costs based on duration and shift type
 *
 * This hook provides a memoized function for calculating the projected cost
 * of a shift based on its duration and the default cost rate for its type.
 * It's typically used in conjunction with useShiftUpdater but can be used
 * independently for cost projections.
 *
 * @param {Array<Object>} shiftTypes - Array of shift types with their default costs
 * @returns {Function} Memoized cost calculation function that takes start time, end time, and shift name
 *
 * @example
 * const calculateShiftCost = useShiftCostCalculation(shiftTypes);
 *
 * // Calculate cost for a specific time range
 * const cost = calculateShiftCost(
 *   new Date('2024-03-20T09:00:00'),
 *   new Date('2024-03-20T17:00:00'),
 *   'Bartender'
 * );
 */
export const useShiftCostCalculation = (shiftTypes) => {
  return useCallback(
    (start, end, shiftName) => {
      const hoursWorked = (new Date(end) - new Date(start)) / (1000 * 60 * 60);
      const shiftType = shiftTypes.find((type) => type.name === shiftName);
      const defaultCost = shiftType?.defaultCost;

      if (defaultCost) {
        return Math.round(hoursWorked * defaultCost);
      }
      return null;
    },
    [shiftTypes]
  );
};
